// src/app/core/services/language.service.ts
import { Injectable, inject, signal } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';

/** Langue de l'interface back-office — distinct des langues patient (établissement.langues). */
export interface UiLanguage {
  code: string;
  label: string;
}

/**
 * Langues disponibles pour l'interface back-office. Volontairement plus restreinte que
 * les langues patient (fr/en/ar/es/it/pt côté backend) : on n'y ajoute un code que
 * lorsque son fichier de traduction (src/assets/i18n/<code>.json) existe réellement,
 * pour ne jamais proposer un choix qui retomberait silencieusement sur le français.
 */
export const AVAILABLE_UI_LANGUAGES: UiLanguage[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
];

const RTL_LANGUAGES = new Set(['ar']);

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);
  // HttpClient construit à partir de HttpBackend (et non injecté directement) : contourne
  // volontairement la chaîne d'intercepteurs (authInterceptor → AuthService → LanguageService),
  // qui créerait sinon une dépendance circulaire (NG0200) au bootstrap de l'app.
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly STORAGE_KEY = 'waitora_locale';
  private readonly TOKEN_KEY = 'waitora_token';

  readonly currentLang = signal<string>(this.getSavedLang());

  constructor() {
    this.applyToDocument(this.currentLang());
    this.translate.use(this.currentLang());
  }

  private getSavedLang(): string {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved && AVAILABLE_UI_LANGUAGES.some(l => l.code === saved)) return saved;
    } catch {
      // localStorage indisponible (navigation privée, etc.) : on reste sur le défaut
    }
    return this.detectBrowserLang();
  }

  /** Utilisé uniquement quand aucune préférence n'a encore été enregistrée (premier lancement, page login). */
  private detectBrowserLang(): string {
    try {
      const navLang = (navigator.language || (navigator as any).userLanguage || '').slice(0, 2).toLowerCase();
      if (navLang && AVAILABLE_UI_LANGUAGES.some(l => l.code === navLang)) return navLang;
    } catch {
      // navigator indisponible (SSR, etc.)
    }
    return 'fr';
  }

  private applyToDocument(lang: string): void {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGUAGES.has(lang) ? 'rtl' : 'ltr';
  }

  /**
   * Change la langue active. `persist` contrôle l'écriture vers le backend (PUT /users/me) :
   * à désactiver quand on applique une valeur qui VIENT déjà du backend (voir syncFromServer),
   * pour ne pas la lui renvoyer inutilement.
   */
  setLang(lang: string, persist = true): void {
    if (lang === this.currentLang() || !AVAILABLE_UI_LANGUAGES.some(l => l.code === lang)) return;
    this.currentLang.set(lang);
    try {
      localStorage.setItem(this.STORAGE_KEY, lang);
    } catch {
      // pas bloquant : la langue reste active pour la session en cours
    }
    this.applyToDocument(lang);
    this.translate.use(lang);
    if (persist) this.persistToServer(lang);
  }

  /** Applique la préférence de langue enregistrée en base (appelé après /auth/me, login, register). */
  syncFromServer(locale: string | null | undefined): void {
    if (locale && AVAILABLE_UI_LANGUAGES.some(l => l.code === locale)) {
      this.setLang(locale, false);
    }
  }

  private persistToServer(lang: string): void {
    let token: string | null = null;
    try {
      token = localStorage.getItem(this.TOKEN_KEY);
    } catch {
      token = null;
    }
    if (!token) return; // pas de compte connecté (ex: page login) : localStorage seul suffit

    this.http.put(`${environment.apiUrl}/api/v1/users/me`, { locale: lang }, {
      headers: { Authorization: `Bearer ${token}` },
    }).subscribe({
      error: () => {
        // Non bloquant : la langue reste appliquée localement même si la sauvegarde échoue
      },
    });
  }
}
